const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
import { useSelector, useDispatch } from 'react-redux'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useDropdown } from '@calendly/ui/components/dropdown'
import styled from 'astroturf/react'
import { Menu, Item } from '@calendly/ui/components/menu'
import { BareButton } from '@calendly/ui/components/bare-button'
import { Button } from '@calendly/ui/components/button'
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons'

import Segment from 'services/analytics/segment'
import { useI18n } from 'locales'
import { setDiagnosticsEnabled } from 'actions/diagnostics'
import { isDiagnosticsAvailableSelector, isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import {
  isPollSelector,
  isAdhocEventSelector
} from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { currentUserSelector } from 'selectors/user'
import { eventService } from 'services/analytics/event_service'
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path'
import { buildAnalyticsProperties } from 'utils/build_analytics_properties'

import { useOneOffOrPollsUrl } from './hooks'
import _ButtonContainer from "menu_dropdown-ButtonContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/publisher_toolbar/booking_page_header_feature/menu_dropdown.js?styleId=ButtonContainer";
import _DropdownButton from "menu_dropdown-DropdownButton.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/publisher_toolbar/booking_page_header_feature/menu_dropdown.js?styleId=DropdownButton";
import _StyledMenu from "menu_dropdown-StyledMenu.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/publisher_toolbar/booking_page_header_feature/menu_dropdown.js?styleId=StyledMenu";


const ButtonContainer = /*#__PURE__*/styled('div', null, {
  displayName: "ButtonContainer",
  styles: _ButtonContainer
})

const DropdownButton = /*#__PURE__*/styled(BareButton, null, {
  displayName: "DropdownButton",
  styles: _DropdownButton
})

const StyledMenu = /*#__PURE__*/styled(Menu, null, {
  displayName: "StyledMenu",
  styles: _StyledMenu,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

export const MenuDropdown = () => {
  const { i18n } = useI18n()
  const isPhone = useDeviceType('isPhone')
  const eventType = useSelector(eventTypeSelector)
  const { id: eventTypeId, kind, type, pooling_type: poolingType } = eventType || {}
  const currentUser = useSelector(currentUserSelector)
  const { id: userId, locale } = currentUser || {}
  const diagnosticsAvailable = useSelector(isDiagnosticsAvailableSelector)
  const diagnosticsEnabled = useSelector(isDiagnosticsEnabledSelector)
  const isAdhocEventType = useSelector(isAdhocEventSelector)
  const isPoll = useSelector(isPollSelector)
  const dispatch = useDispatch()
  const [toggleDropdown, Dropdown, isOpen] = useDropdown()
  const enableDiagnostics = () => dispatch(setDiagnosticsEnabled(true))
  const disableDiagnostics = () => dispatch(setDiagnosticsEnabled(false))
  const meetingTypesRoute = useMeetingTypesPath()
  const pendingEventsUrl = useOneOffOrPollsUrl()

  const trackEdit = () => {
    const baseProperties = {
      destination: 'edit',
      location: 'booking page menu',
      event_type_kind: kind,
      event_type_pooling_type: poolingType,
      event_type_type: type
    }

    const analyticsProperties = buildAnalyticsProperties({
      entities: ['event_type', 'global', 'org', 'user'],
      eventType,
      currentUser,
      additionalProperties: baseProperties
    })

    // Existing v2 (server-side)
    eventService.dispatch({
      name: 'Event Type Editor Open',
      version: 1,
      dependencies: { user_id: userId, event_type_id: eventTypeId },
      entities: ['event_type', 'user', 'organization'],
      properties: baseProperties
    })

    // Segment v1 (device-mode) for Braze
    Segment.track('Event Type Editor Open', analyticsProperties)
  }

  const sendToSegment = (actionString) => {
    Segment.track(actionString, {
      platform_type: 'web_app',
    })
  }

  const handleEdit = () => {
    Segment.track('event_type.edit', {
      event_type_id: eventType.id,
      action: 'edit_booking_page',
      event_type_kind: eventType.kind,
      type: eventType.type,
      pooling_type: eventType.pooling_type
    })

    sendToSegment('booking_menu_edit.clicked')
    trackEdit()
  }

  const trackAndHandleMenuClick = () => {
    if (!isOpen) {
      sendToSegment('booking_menu.menu_opened')
    }
    toggleDropdown()
  }

  const trackHomeClick = () => sendToSegment('booking_menu_home.clicked')

  const trackAndHandleTroubleshootClick = () => {
    if (diagnosticsEnabled) {
      sendToSegment('booking_menu_troubleshoot_stop_availability.clicked')
      disableDiagnostics()
    }

    if (!diagnosticsEnabled) {
      sendToSegment('booking_menu_troubleshoot_availability.clicked')
      enableDiagnostics()
    }
  }

  return (
    <>
      <ButtonContainer>
        <DropdownButton
          onClick={trackAndHandleMenuClick}
          isopen={isOpen.toString()}
          role="button"
          aria-label="Menu"
          aria-expanded={isOpen}
          decoration="ghost"
        >
          {i18n.t('publisher.diagnostics.publisher_toolbar.menu', { locale })}
          {isOpen ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
        </DropdownButton>
      </ButtonContainer>
      <Dropdown>
        <StyledMenu>
          <Item as="a" href={meetingTypesRoute} onClick={trackHomeClick}>{i18n.t('publisher.menu.home', { locale })}</Item>
          {!isAdhocEventType && eventType.policy?.edit && (
            <Item as="a" href={eventType.edit_path} onClick={handleEdit}>
              {i18n.t('publisher.diagnostics.publisher_toolbar.edit_event_type', { locale })}
            </Item>
          )}
          {isAdhocEventType && (
            <Item as="a" href={pendingEventsUrl}>
              {isPoll
                ? i18n.t('publisher.scheduled_events.pending.poll_results.view_poll_results', { locale })
                : i18n.t('publisher.scheduled_events.pending.view_pending_one_off', { locale })}
            </Item>
          )}
          {diagnosticsAvailable && (
            diagnosticsEnabled
              ? (
                <Item onClick={trackAndHandleTroubleshootClick}>
                  {i18n.t('publisher.diagnostics.publisher_toolbar.disable_diagnostics', { locale })}
                </Item>
              )
              : (
                <Item onClick={trackAndHandleTroubleshootClick}>
                  {i18n.t('publisher.diagnostics.publisher_toolbar.enable_diagnostics', { locale })}
                </Item>
              )
          )}
          {isPhone && (
            <Item>
              <Button
                decoration="secondary-outline"
                onClick={toggleDropdown}
              >
                {i18n.t('booking.button.close')}
              </Button>
            </Item>
          )}
        </StyledMenu>
      </Dropdown>
    </>
  )
}
